import React, { useState, useEffect, useRef } from 'react';
// import { appInsights, reactPlugin } from '../../../appInsights';
// import { withAITracking } from '@microsoft/applicationinsights-react-js';
import { jsonEditorStyles } from '../jsonEditorStyles';
import cn from 'clsx';
import Button from '@mui/material/Button';
import FormControl from '@mui/material/FormControl';
import { SelectChangeEvent } from '@mui/material';
import { Box, Typography, Checkbox, FormControlLabel } from '@mui/material';
import { useSelector } from 'react-redux';
import { getJSONAADAttributes, getNodeData } from '../../../store';
import { SelectComponent, TextComponent } from '../../uiComponents';
import { OptionProps } from '../../../view-models';
import jsonComponentsConfig from '../../../constants/jsonComponents';
import useComponentUIHook from './useComponentUIHook';
import SessionManagement, { sessionFieldsAreValid } from './sessionManagement';
import { cloneDeep } from 'lodash';
import { ResolverSelector, resolverStringValid } from './resolverSelector';

interface componentProps {
  componentData: any;
  closeGritComponent: any;
  setUnsavedChanges: any;
}

const GenerateAADUpdateUI = ({ componentData, closeGritComponent, setUnsavedChanges }: componentProps) => {
  const initAttribute = useRef(componentData['attribute']);
  const [selClaims, setSelClaims] = useState<string[]>([]);
  const [selattributes, setSelAttributes] = useState<string[]>([]);
  const configData = jsonComponentsConfig.aadUpdate.data;
  const { modifiedData, setModifiedData, checkIfSameIgnoringSubFlows, allClaims } = useComponentUIHook(componentData);
  const [disableSubmit, setDisableSubmit] = useState<boolean>(false);
  const [enableMapBt, setEnableMapBt] = useState(false);
  const allAADAttributes = useSelector(getJSONAADAttributes);
  const allNodeData = useSelector(getNodeData);
  const classes = jsonEditorStyles();

  useEffect(() => {
    const mapCA = allNodeData[componentData.id]?.attributes || [];
    setSelClaims(mapCA.map((m: any) => m.claim));
    setSelAttributes(mapCA.map((m: any) => m.id));
  }, [allNodeData]);

  useEffect(() => {
    const _res: boolean = checkIfSameIgnoringSubFlows();
    setDisableSubmit(_res || (modifiedData?.indexProperty?.id === '' ||
    modifiedData?.indexProperty?.claim === '' ||
    (modifiedData.mapObjectId && modifiedData.mappedClaim === '') ||
    modifiedData?.attributes?.reduce((prev: boolean, current: any) => {
      return (
        prev ||
        (current?.claimOrFunc === 'Claim' && current?.claim === '') ||
        (current?.claimOrFunc === 'Function' &&
          (current?.transformation === '' || current?.claim === '' || current?.claim2 === '')) ||
        (current?.claimOrFunc === 'Session Context' && !resolverStringValid(current?.resolver))
      );
    }, false)) ||
    (!sessionFieldsAreValid(modifiedData)));
    setUnsavedChanges(_res);
    setEnableMapBt(
      (modifiedData['attribute']['claim'] ||
        (modifiedData['attribute'].claimOrFunc === 'Session Context' && modifiedData['attribute']['resolver'])) &&
        modifiedData['attribute']['id'],
    );
    setSelClaims(
      modifiedData?.indexProperty?.claim
        ? modifiedData?.attributes.map((m: any) => m.claim).concat([modifiedData?.indexProperty?.claim])
        : modifiedData?.attributes.map((m: any) => m.claim),
    );
    setSelAttributes(modifiedData?.attributes.map((m: any) => m.id));
  }, [modifiedData]);

  return (
    <div>
      <FormControl required sx={{ m: 1, minWidth: 600 }}>
        <div className={classes.flexRow}>
          <SelectComponent
            changeHandler={(e: SelectChangeEvent<string>) => {
              setModifiedData({
                ...modifiedData,
                ['indexProperty']: {
                  id: e.target.value,
                  dataType:
                    configData.indexProperty.dataTypes[
                      configData.indexProperty.values.findIndex((el: any) => el === e.target.value)
                    ],
                  claim: '',
                },
              });
            }}
            data={{
              value: modifiedData.indexProperty?.id || '',
              label: configData.indexProperty.displayName,
              options: configData.indexProperty.values.map((v) => ({ label: v, value: v })),
              style: { margin: 1, width: 200 },
              required: true,
            }}
          />
          <SelectComponent
            changeHandler={(e: SelectChangeEvent<string>) => {
              setSelClaims((prv) =>
                prv.find((p) => p === e.target.value)
                  ? prv
                  : prv.filter((i) => i !== modifiedData?.indexProperty?.claim).concat(e.target.value),
              );
              setModifiedData({
                ...modifiedData,
                ['indexProperty']: { ...modifiedData.indexProperty, claim: e.target.value },
              });
            }}
            data={{
              value: modifiedData?.indexProperty?.claim,
              label: 'Claim',
              style: { margin: 1, width: 200 },
              options: Object.keys(allClaims)
                .filter(
                  (c) =>
                    !selClaims.find((s) => s === c) && allClaims[c]?.dataType === modifiedData.indexProperty.dataType,
                )
                .reduce(
                  (acc: OptionProps[], curVal: string) => acc.concat({ label: curVal, value: curVal }),
                  modifiedData?.indexProperty?.claim
                    ? [
                        {
                          label: modifiedData?.indexProperty?.claim,
                          value: modifiedData?.indexProperty?.claim,
                        },
                      ]
                    : [],
                )
                .sort((a: OptionProps, b: OptionProps) => (a.value.toLowerCase() > b.value.toLowerCase() ? 1 : -1)),
              required: true,
            }}
          />
        </div>
        <Box>
          <Typography gutterBottom variant="body1">
            Mapping of aad property to tenant attribute
          </Typography>
          <div>
            {modifiedData?.attributes.map((map: any, index: number) => {
              return (
                <div className={classes.flexRow} key={map.claim + map.id}>
                  <SelectComponent
                    changeHandler={(e: SelectChangeEvent<string>) => {
                      const _temp = cloneDeep(modifiedData);
                      _temp['attributes'][index]['id'] = e.target.value;
                      if (['userIdentities'].includes(e.target.value)) {
                        _temp['attributes'][index]['operation'] = 'insert';
                      } else delete _temp['attributes'][index]['operation'];
                      setModifiedData(_temp);
                    }}
                    data={{
                      value: map.id,
                      label: 'AAD Attribute',
                      required: true,
                      style: { margin: 1, width: 200 },
                      options: Object.keys(allAADAttributes)
                        .filter(
                          (a) =>
                            !selattributes.find((s) => s === a) &&
                            allAADAttributes[a]?.dataType ===
                              (allClaims[map.claim]?.dataType || allAADAttributes[a]?.dataType) &&
                            allAADAttributes[a]?.dataType != 'userIdentityCollection',
                        )
                        .reduce(
                          (acc: OptionProps[], curVal: string) => acc.concat({ label: curVal, value: curVal }),
                          [{ label: map.id, value: map.id }],
                        )
                        .sort((a: OptionProps, b: OptionProps) =>
                          a.value.toLowerCase() > b.value.toLowerCase() ? 1 : -1,
                        ),
                    }}
                  />
                  {(allAADAttributes[map.id]['dataType'] === 'string' ||
                    allAADAttributes[map.id]['dataType'] === 'dateTime') &&
                    map.id !== 'password' && (
                      <>
                        <SelectComponent
                          changeHandler={(e: SelectChangeEvent<string>) => {
                            const _temp = cloneDeep(modifiedData);
                            _temp['attributes'][index]['claimOrFunc'] = e.target.value;
                            if (e.target.value === 'Claim') {
                              delete _temp['attributes'][index]['transformation'];
                              delete _temp['attributes'][index]['claim2'];
                              delete _temp['attributes'][index]['resolver'];
                              if (allAADAttributes[map.id]['dataType'] === 'string') {
                                _temp['attributes'][index]['defaultValue'] = '';
                              }
                              _temp['attributes'][index]['claim'] = '';
                            } else if (e.target.value === 'Session Context') {
                              delete _temp['attributes'][index]['transformation'];
                              delete _temp['attributes'][index]['claim2'];
                              delete _temp['attributes'][index]['defaultValue'];
                              delete _temp['attributes'][index]['claim'];
                              _temp['attributes'][index]['resolver'] = '';
                            } else {
                              delete _temp['attributes'][index]['resolver'];
                              delete _temp['attributes'][index]['defaultValue'];
                              _temp['attributes'][index]['transformation'] = '';
                              _temp['attributes'][index]['claim2'] = '';
                              _temp['attributes'][index]['claim'] = '';
                            }
                            setModifiedData(_temp);
                          }}
                          data={{
                            value: modifiedData?.attributes[index].claimOrFunc || '',
                            label: 'Select Type',
                            required: true,
                            style: { margin: 1, width: 200 },
                            options:
                              allAADAttributes[map.id]['dataType'] === 'string'
                                ? [
                                    { label: 'Claim', value: 'Claim' },
                                    { label: 'Function', value: 'Function' },
                                    { label: 'Session Context', value: 'Session Context' },
                                  ]
                                : [
                                    { label: 'Claim', value: 'Claim' },
                                    { label: 'Session Context', value: 'Session Context' },
                                  ],
                          }}
                        />
                        {modifiedData?.attributes[index].claimOrFunc === 'Function' && (
                          <SelectComponent
                            changeHandler={(e: SelectChangeEvent<string>) => {
                              const _temp = cloneDeep(modifiedData);
                              _temp['attributes'][index]['transformation'] = e.target.value;
                              setModifiedData(_temp);
                            }}
                            data={{
                              value: modifiedData?.attributes[index].transformation || '',
                              label: 'Function Name',
                              required: true,
                              options: Object.keys(configData.transformation).reduce(
                                (acc: OptionProps[], curVal: string) => acc.concat({ label: curVal, value: curVal }),
                                [],
                              ),
                            }}
                          />
                        )}
                        {modifiedData?.attributes[index].claimOrFunc === 'Session Context' && (
                          <ResolverSelector
                            changeHandler={(rValue: string) => {
                              const _temp = cloneDeep(modifiedData);
                              _temp['attributes'][index]['resolver'] = rValue;
                              setModifiedData(_temp);
                            }}
                            value={modifiedData?.attributes[index].resolver || ''}
                            label="Claim Resolver"
                            data={{
                              required: true,
                            }}
                          />
                        )}
                      </>
                    )}
                  {modifiedData?.attributes[index].claimOrFunc !== 'Session Context' && (
                    <SelectComponent
                      changeHandler={(e: SelectChangeEvent<string>) => {
                        const _temp = cloneDeep(modifiedData);
                        _temp['attributes'][index]['claim'] = e.target.value;
                        setModifiedData(_temp);
                      }}
                      data={{
                        value: map.claim,
                        label: 'Claim',
                        required: true,
                        style: { margin: 1, width: 200 },
                        options: Object.keys(allClaims)
                          .filter(
                            (c) =>
                              // !selClaims.find((s) => s === c) &&
                              (allAADAttributes[map.id]?.dataType !== 'userIdentityCollection' &&
                                (allAADAttributes[map.id]?.dataType || allClaims[c]?.dataType) ===
                                  allClaims[c]?.dataType) ||
                              (allAADAttributes[map.id]?.dataType === 'userIdentityCollection' &&
                                allClaims[c]?.dataType === 'userIdentity'),
                          )
                          .reduce(
                            (acc: OptionProps[], curVal: string) => acc.concat({ label: curVal, value: curVal }),
                            [],
                          )
                          .sort((a: OptionProps, b: OptionProps) =>
                            a.value.toLowerCase() > b.value.toLowerCase() ? 1 : -1,
                          ),
                      }}
                    />
                  )}
                  {(allAADAttributes[map.id]['dataType'] === 'boolean' ||
                    (allAADAttributes[map.id]['dataType'] === 'string' &&
                      modifiedData?.attributes[index].claimOrFunc === 'Claim')) && (
                    <>
                      {allAADAttributes[map.id]['dataType'] === 'boolean' && (
                        <SelectComponent
                          changeHandler={(e: SelectChangeEvent<string>) => {
                            const _temp = cloneDeep(modifiedData);
                            _temp['attributes'][index]['defaultValue'] = e.target.value;
                            setModifiedData(_temp);
                          }}
                          data={{
                            value: modifiedData.attributes[index]['defaultValue'] || '',
                            label: 'Default Value',
                            options: [
                              { label: 'true', value: 'true' },
                              { label: 'false', value: 'false' },
                              { label: 'none', value: '' },
                            ],
                            style: { margin: 1, width: 200 },
                          }}
                        />
                      )}
                      {allAADAttributes[map.id]['dataType'] === 'string' && (
                        <TextComponent
                          changeHandler={(e: React.ChangeEvent<HTMLTextAreaElement | HTMLInputElement>) => {
                            const _temp = cloneDeep(modifiedData);
                            _temp['attributes'][index]['defaultValue'] = e.target.value;
                            setModifiedData(_temp);
                          }}
                          data={{
                            value: modifiedData.attributes[index]['defaultValue'] || '',
                            label: 'Default Value',
                            style: { margin: 1, width: 200 },
                          }}
                        />
                      )}
                      <FormControlLabel
                        label="Always Use Default Value"
                        control={
                          <Checkbox
                            checked={modifiedData.attributes[index]['alwaysUseDefaultValue']}
                            onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                              const _temp = cloneDeep(modifiedData);
                              _temp['attributes'][index]['alwaysUseDefaultValue'] =
                                !_temp['attributes'][index]['alwaysUseDefaultValue'];
                              setModifiedData(_temp);
                            }}
                          />
                        }
                      />
                    </>
                  )}
                  {['userIdentities'].includes(map.id) && (
                    <SelectComponent
                      changeHandler={(e: SelectChangeEvent<string>) => {
                        const _temp = cloneDeep(modifiedData);
                        _temp['attributes'][index]['operation'] = e.target.value;
                        setModifiedData(_temp);
                      }}
                      data={{
                        value: map?.operation,
                        label: 'Operation',
                        style: { margin: 1, width: 200 },
                        options: [
                          { label: 'insert', value: 'insert' },
                          { label: 'replace', value: 'replace' },
                          { label: 'remove', value: 'remove' },
                        ],
                      }}
                    />
                  )}
                  {modifiedData['attributes'][index]['transformation'] === 'string concatenation' && (
                    <SelectComponent
                      changeHandler={(e: SelectChangeEvent<string>) => {
                        const _temp = cloneDeep(modifiedData);
                        _temp['attributes'][index]['claim2'] = e.target.value;
                        setModifiedData(_temp);
                      }}
                      data={{
                        value: modifiedData?.attributes[index].claim2 || '',
                        label: 'Claim',
                        required: true,
                        style: { margin: 1, width: 200 },
                        options: Object.keys(allClaims)
                          .filter(
                            (c) =>
                              // !selClaims.find((s) => s === c) &&
                              (allAADAttributes[modifiedData?.attributes[index].id].dataType ||
                                allClaims[c]?.dataType) === allClaims[c]?.dataType,
                          )
                          .reduce(
                            (acc: OptionProps[], curVal: string) => acc.concat({ label: curVal, value: curVal }),
                            [],
                          )
                          .sort((a: OptionProps, b: OptionProps) =>
                            a.value.toLowerCase() > b.value.toLowerCase() ? 1 : -1,
                          ),
                      }}
                    />
                  )}
                  <div>
                    <Button
                      type="submit"
                      fullWidth
                      variant="contained"
                      sx={{ mt: 1, mb: 2 }}
                      onClick={() => {
                        const _temp = cloneDeep(modifiedData);
                        _temp['attributes'].splice(index, 1);
                        setModifiedData(_temp);
                      }}
                    >
                      Delete
                    </Button>
                  </div>
                </div>
              );
            })}
          </div>
          <div className={classes.flexRow}>
            <SelectComponent
              changeHandler={(e: SelectChangeEvent<string>) => {
                const _attr = { ...modifiedData['attribute'], id: e.target.value };
                if (['userIdentities'].includes(e.target.value)) {
                  _attr['operation'] = 'insert';
                } else delete _attr['operation'];
                if (allAADAttributes[_attr.id]?.dataType === 'dateTime' && _attr.claimOrFunc === 'Function') {
                  _attr.claimOrFunc = '';
                  delete _attr['transformation'];
                  delete _attr['claim2'];
                }
                if (allAADAttributes[_attr.id]?.dataType === 'boolean' && _attr.claimOrFunc) {
                  _attr.claimOrFunc = '';
                  delete _attr['transformation'];
                  delete _attr['claim2'];
                  delete _attr['resolver'];
                }
                setModifiedData({ ...modifiedData, attribute: _attr });
              }}
              data={{
                value: modifiedData?.attribute?.id,
                label: 'AAD Attribute',
                style: { margin: 1, width: 200 },
                options: Object.keys(allAADAttributes)
                  .filter((a) => {
                    const res =
                      !selattributes.find((s) => s === a) &&
                      allAADAttributes[a]?.dataType ===
                        (allClaims[modifiedData?.attribute?.claim]?.dataType || allAADAttributes[a]?.dataType) &&
                      allAADAttributes[a]?.dataType != 'userIdentityCollection' &&
                      (allAADAttributes[a]?.dataType !== 'dateTime' ||
                        modifiedData?.attribute?.claimOrFunc !== 'Function');
                    return res;
                  })
                  .reduce(
                    (acc: OptionProps[], curVal: string) => acc.concat({ label: curVal, value: curVal }),
                    modifiedData['indexProperty']['id'] !== 'foreign objectid'
                      ? [{ label: 'userIdentities', value: 'userIdentities' }]
                      : [],
                  )
                  .sort((a: OptionProps, b: OptionProps) => (a.value.toLowerCase() > b.value.toLowerCase() ? 1 : -1)),
              }}
            />
            {allAADAttributes.hasOwnProperty(modifiedData?.attribute?.id) &&
              (allAADAttributes[modifiedData?.attribute?.id]['dataType'] === 'string' ||
                allAADAttributes[modifiedData?.attribute?.id]['dataType'] === 'dateTime') &&
              modifiedData?.attribute?.id !== 'password' && (
                <>
                  <SelectComponent
                    changeHandler={(e: SelectChangeEvent<string>) => {
                      const _temp = cloneDeep(modifiedData);
                      _temp['attribute']['claimOrFunc'] = e.target.value;
                      if (e.target.value === 'Claim') {
                        delete _temp['attribute']['transformation'];
                        delete _temp['attribute']['claim2'];
                        delete _temp['attribute']['resolver'];
                        _temp['attribute']['claim'] = '';
                        if (allAADAttributes[modifiedData?.attribute?.id]['dataType'] === 'string') {
                          _temp['attribute']['defaultValue'] = '';
                        }
                      } else if (e.target.value === 'Session Context') {
                        delete _temp['attribute']['transformation'];
                        delete _temp['attribute']['claim2'];
                        delete _temp['attribute']['defaultValue'];
                        delete _temp['attribute']['claim'];
                        _temp['attribute']['resolver'] = '';
                      } else {
                        delete _temp['attribute']['defaultValue'];
                        delete _temp['attribute']['resolver'];
                        _temp['attribute']['transformation'] = '';
                        _temp['attribute']['claim2'] = '';
                        _temp['attribute']['claim'] = '';
                      }
                      setModifiedData(_temp);
                    }}
                    data={{
                      value: modifiedData?.attribute.claimOrFunc || '',
                      label: 'Select Type',
                      required: true,
                      style: { margin: 1, width: 200 },
                      options:
                        allAADAttributes[modifiedData?.attribute?.id]['dataType'] === 'string'
                          ? [
                              { label: 'Claim', value: 'Claim' },
                              { label: 'Function', value: 'Function' },
                              { label: 'Session Context', value: 'Session Context' },
                            ]
                          : [
                              { label: 'Claim', value: 'Claim' },
                              { label: 'Session Context', value: 'Session Context' },
                            ],
                    }}
                  />
                  {modifiedData?.attribute.claimOrFunc === 'Function' && (
                    <SelectComponent
                      changeHandler={(e: SelectChangeEvent<string>) => {
                        const _temp = cloneDeep(modifiedData);
                        _temp['attribute']['transformation'] = e.target.value;
                        setModifiedData(_temp);
                      }}
                      data={{
                        value: modifiedData?.attribute.transformation || '',
                        label: 'Function Name',
                        required: true,
                        options: Object.keys(configData.transformation).reduce(
                          (acc: OptionProps[], curVal: string) => acc.concat({ label: curVal, value: curVal }),
                          [],
                        ),
                      }}
                    />
                  )}
                  {modifiedData?.attribute.claimOrFunc === 'Session Context' && (
                    <ResolverSelector
                      changeHandler={(rValue: string) => {
                        const _temp = cloneDeep(modifiedData);
                        _temp['attribute']['resolver'] = rValue;
                        setModifiedData(_temp);
                      }}
                      value={modifiedData?.attribute.resolver || ''}
                      label="Claim Resolver"
                      data={{
                        required: true,
                      }}
                    />
                  )}
                </>
              )}
            {modifiedData?.attribute?.claimOrFunc !== 'Session Context' && (
              <SelectComponent
                changeHandler={(e: SelectChangeEvent<string>) => {
                  setModifiedData({
                    ...modifiedData,
                    attribute: { ...modifiedData['attribute'], claim: e.target.value },
                  });
                }}
                data={{
                  value: modifiedData?.attribute?.claim,
                  label: 'Claim',
                  style: { margin: 1, width: 200 },
                  options: Object.keys(allClaims)
                    .filter(
                      (c) =>
                        // !selClaims.find((s) => s === c) &&
                        (allAADAttributes[modifiedData?.attribute?.id]?.dataType !== 'userIdentityCollection' &&
                          (allAADAttributes[modifiedData?.attribute?.id]?.dataType || allClaims[c]?.dataType) ===
                            allClaims[c]?.dataType) ||
                        (allAADAttributes[modifiedData?.attribute?.id]?.dataType === 'userIdentityCollection' &&
                          allClaims[c]?.dataType === 'userIdentity'),
                    )
                    .reduce((acc: OptionProps[], curVal: string) => acc.concat({ label: curVal, value: curVal }), [])
                    .sort((a: OptionProps, b: OptionProps) => (a.value.toLowerCase() > b.value.toLowerCase() ? 1 : -1)),
                }}
              />
            )}
            {modifiedData?.attribute?.id &&
              (allAADAttributes[modifiedData?.attribute.id]['dataType'] === 'boolean' ||
                (allAADAttributes[modifiedData?.attribute.id]['dataType'] === 'string' &&
                  modifiedData?.attribute?.claimOrFunc === 'Claim')) && (
                <>
                  {allAADAttributes[modifiedData['attribute']['id']]['dataType'] === 'boolean' && (
                    <SelectComponent
                      changeHandler={(e: SelectChangeEvent<string>) => {
                        const _temp = cloneDeep(modifiedData);
                        _temp['attribute']['defaultValue'] = e.target.value;
                        setModifiedData(_temp);
                      }}
                      data={{
                        value: modifiedData['attribute']['defaultValue'] || '',
                        label: 'Default Value',
                        options: [
                          { label: 'true', value: 'true' },
                          { label: 'false', value: 'false' },
                          { label: 'none', value: '' },
                        ],
                        style: { margin: 1, width: 200 },
                      }}
                    />
                  )}
                  {allAADAttributes[modifiedData['attribute']['id']]['dataType'] === 'string' && (
                    <TextComponent
                      changeHandler={(e: React.ChangeEvent<HTMLTextAreaElement | HTMLInputElement>) => {
                        const _temp = cloneDeep(modifiedData);
                        _temp['attribute']['defaultValue'] = e.target.value;
                        setModifiedData(_temp);
                      }}
                      data={{
                        value: modifiedData['attribute']['defaultValue'] || '',
                        label: 'Default Value',
                        style: { margin: 1, width: 200 },
                      }}
                    />
                  )}
                  <FormControlLabel
                    label="Always Use Default Value"
                    control={
                      <Checkbox
                        checked={modifiedData['attribute']['alwaysUseDefaultValue']}
                        onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                          const _temp = cloneDeep(modifiedData);
                          _temp['attribute']['alwaysUseDefaultValue'] = !_temp['attribute']['alwaysUseDefaultValue'];
                          setModifiedData(_temp);
                        }}
                      />
                    }
                  />
                </>
              )}
            {['userIdentities'].includes(modifiedData['attribute']['id']) && (
              <SelectComponent
                changeHandler={(e: SelectChangeEvent<string>) => {
                  setModifiedData({
                    ...modifiedData,
                    attribute: { ...modifiedData['attribute'], operation: e.target.value },
                  });
                }}
                data={{
                  value: modifiedData?.attribute?.operation,
                  label: 'Operation',
                  style: { margin: 1, width: 200 },
                  options: [
                    { label: 'insert', value: 'insert' },
                    { label: 'replace', value: 'replace' },
                    { label: 'remove', value: 'remove' },
                  ],
                }}
              />
            )}

            {modifiedData['attribute']['transformation'] === 'string concatenation' && (
              <SelectComponent
                changeHandler={(e: SelectChangeEvent<string>) => {
                  const _temp = cloneDeep(modifiedData);
                  _temp['attribute']['claim2'] = e.target.value;
                  setModifiedData(_temp);
                }}
                data={{
                  value: modifiedData?.attribute.claim2 || '',
                  label: 'Claim',
                  required: true,
                  style: { margin: 1, width: 200 },
                  options: Object.keys(allClaims)
                    .filter(
                      (c) =>
                        // !selClaims.find((s) => s === c) &&
                        (allAADAttributes[modifiedData?.attribute.id].dataType || allClaims[c]?.dataType) ===
                        allClaims[c]?.dataType,
                    )
                    .reduce((acc: OptionProps[], curVal: string) => acc.concat({ label: curVal, value: curVal }), [])
                    .sort((a: OptionProps, b: OptionProps) => (a.value.toLowerCase() > b.value.toLowerCase() ? 1 : -1)),
                }}
              />
            )}
            <div>
              <Button
                type="submit"
                fullWidth
                variant="contained"
                sx={{ mt: 1, mb: 2 }}
                onClick={() => {
                  setModifiedData((prv: any) => ({
                    ...modifiedData,
                    attributes: [...modifiedData['attributes'], modifiedData.attribute],
                    attribute: initAttribute.current,
                  }));
                }}
                disabled={!enableMapBt}
              >
                + Map
              </Button>
            </div>
          </div>
          <div className={classes.flexRow}>
            <FormControlLabel
              sx={{ m: 0 }}
              label={configData.mapObjectId.displayName}
              control={
                <Checkbox
                  checked={modifiedData['mapObjectId']}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                    setModifiedData({
                      ...modifiedData,
                      mapObjectId: !modifiedData['mapObjectId'],
                    });
                  }}
                />
              }
            />
            {modifiedData['mapObjectId'] && (
              <SelectComponent
                changeHandler={(e: SelectChangeEvent<string>) => {
                  setModifiedData({ ...modifiedData, mappedClaim: e.target.value });
                }}
                data={{
                  value: modifiedData?.mappedClaim,
                  label: 'Claim',
                  style: { margin: 1, width: 200 },
                  options: Object.keys(allClaims)
                    .filter((c) => allClaims[c]?.dataType === 'string')
                    .reduce((acc: OptionProps[], curVal: string) => acc.concat({ label: curVal, value: curVal }), [])
                    .sort((a: OptionProps, b: OptionProps) => (a.value.toLowerCase() > b.value.toLowerCase() ? 1 : -1)),
                  required: true,
                }}
              />
            )}
          </div>
        </Box>
        {modifiedData['subflowParent'] && (
          <TextComponent
            changeHandler={(e: React.ChangeEvent<HTMLTextAreaElement | HTMLInputElement>) => {
              const _temp = cloneDeep(modifiedData);
              _temp['errorMessage'] = e.target.value;
              setModifiedData(_temp);
            }}
            data={{
              label: 'Custom Error Message if User Does Not Exist',
              value: modifiedData['errorMessage'] ?? '',
            }}
          />
        )}
        {configData.sessionManagement && (
          <SessionManagement
            modifiedData={modifiedData}
            changeHandler={(data: any) => setModifiedData(data)}
            sessionTypes={configData.sessionTypes}
          />
        )}
        <Button
          type="submit"
          fullWidth
          variant="contained"
          sx={{ mt: 3, mb: 2 }}
          onClick={() => {
            closeGritComponent(cloneDeep(modifiedData));
          }}
          disabled={disableSubmit}
        >
          Save
        </Button>
      </FormControl>
    </div>
  );
};

// export default withAITracking(reactPlugin, GenerateAADUpdateUI);
export default GenerateAADUpdateUI;
